import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { Task, Project, Epic } from '../types';
import { useApp } from '../providers/AppProvider';
import { format, addDays, differenceInCalendarDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isValid } from 'date-fns';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike } from '../utils/dateUtils';
import SurfaceCard from '../components/SurfaceCard';
import { getStatusColors } from '../constants/colorScheme';

const normalizeDurationDays = (estimatedDays?: number): number | null => {
  if (typeof estimatedDays !== 'number' || !Number.isFinite(estimatedDays) || estimatedDays <= 0) {
    return null;
  }

  return Math.max(1, Math.ceil(estimatedDays));
};

const TODAY_HIGHLIGHT_CLASS = 'bg-primary/10';
const GRID_CELL_CLASS = 'flex h-10 w-8 flex-shrink-0 items-center border-r border-border/50';
const TASK_LEGEND_STATUSES = [
  'Not Started',
  'Active',
  'In Progress',
  'In Review',
  'Completed',
  'Blocked',
  'Deferred',
] as const;

export default function GanttChart() {
  const { projectId } = useParams();
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'days' | 'weeks' | 'months'>('days');
  const { state, loading, currentDate } = useApp();
  const { projects, tasks, epics } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
    epics: any[];
  };

  const project = useMemo(() => {
      return projects.find(
        (p: Project) => String(p.id).toLowerCase() === String(projectId).toLowerCase()
      );
    }, [projects, projectId]);
  
  if (loading && !project) {
    return <div className="p-8 text-center">Loading project...</div>;
  }

  const projectTasks = tasks.filter((t) => t.projectId === projectId);
  const projectEpics = epics.filter((e) => e.projectId === projectId) as Epic[];

  // Calculate date range
  const today = currentDate;
  const allDates: Date[] = [];

  const parsedProjectStart = parseDateLike(project?.startDate);
  const parsedProjectEnd = parseDateLike(project?.targetEndDate);

  if (parsedProjectStart) allDates.push(parsedProjectStart);
  if (parsedProjectEnd) allDates.push(parsedProjectEnd);

  projectEpics.forEach((epic) => {
    const parsedEpicEnd = parseDateLike(epic.targetEndDate);
    if (parsedEpicEnd) allDates.push(parsedEpicEnd);
  });
  
  projectTasks?.forEach(task => {
    const parsedStart = parseDateLike(task.startDate);
    const parsedEnd = parseDateLike(task.targetEndDate);
    const parsedCreatedAt = parseDateLike(task.createdAt);

    if (parsedStart) allDates.push(parsedStart);
    if (parsedEnd) allDates.push(parsedEnd);
    if (parsedCreatedAt) allDates.push(parsedCreatedAt);
  });

  const startDate = allDates.length > 0 
    ? new Date(Math.min(...allDates.map(d => d.getTime())))
    : startOfMonth(today);
  
  const endDate = allDates.length > 0
    ? new Date(Math.max(...allDates.map(d => d.getTime())))
    : endOfMonth(addDays(today, 90));

  const totalDurationDays = Math.max(1, differenceInCalendarDays(endDate, startDate) + 1);

  // Generate timeline columns
  const timelineDays = eachDayOfInterval({ start: startDate, end: endDate });

  const getTaskRange = (task: Task): { start: Date; end: Date } => {
    const parsedStart = parseDateLike(task.startDate);
    const parsedEnd = parseDateLike(task.targetEndDate);
    const parsedCreatedAt = parseDateLike(task.createdAt) || today;
    const durationDays = normalizeDurationDays(task.estimatedDays);

    const start = parsedStart
      || (parsedEnd && durationDays ? addDays(parsedEnd, -(durationDays - 1)) : parsedEnd)
      || parsedCreatedAt;

    const end = parsedEnd
      || (durationDays ? addDays(start, durationDays - 1) : addDays(start, 6));

    if (end < start) {
      return { start: end, end: start };
    }

    return { start, end };
  };

  const getTaskDurationDays = (task: Task): number => {
    const { start, end } = getTaskRange(task);
    return Math.max(1, differenceInCalendarDays(end, start) + 1);
  };
  
  const getTaskStartDate = (task: Task): Date => {
    return getTaskRange(task).start;
  };

  const getTaskEndDate = (task: Task): Date => {
    return getTaskRange(task).end;
  };

  const getRangePosition = (
    range: { start: Date; end: Date } | null,
    day: Date
  ): 'start' | 'middle' | 'end' | null => {
    if (!range) return null;

    if (isSameDay(day, range.start) && isSameDay(day, range.end)) return 'start';
    if (isSameDay(day, range.start)) return 'start';
    if (isSameDay(day, range.end)) return 'end';
    if (day > range.start && day < range.end) return 'middle';
    return null;
  };

  const getEpicRange = (epic: Epic, epicTasks: Task[]): { start: Date; end: Date } | null => {
    const taskRanges = epicTasks.map((task) => getTaskRange(task));
    const taskStartDates = taskRanges.map((range) => range.start);
    const taskEndDates = taskRanges.map((range) => range.end);

    const epicEndDate = parseDateLike(epic.targetEndDate);
    const projectStartDate = parseDateLike(project?.startDate);

    const startCandidates = [...taskStartDates];
    const endCandidates = [...taskEndDates];

    if (epicEndDate) {
      endCandidates.push(epicEndDate);
    }

    if (startCandidates.length === 0 && projectStartDate) {
      startCandidates.push(projectStartDate);
    }

    if (startCandidates.length === 0 && epicEndDate) {
      startCandidates.push(epicEndDate);
    }

    if (startCandidates.length === 0 || endCandidates.length === 0) {
      return null;
    }

    const start = new Date(Math.min(...startCandidates.map((d) => d.getTime())));
    const end = new Date(Math.max(...endCandidates.map((d) => d.getTime())));

    if (end < start) {
      return { start: end, end: start };
    }

    return { start, end };
  };

  const getEpicDurationDays = (epicRange: { start: Date; end: Date } | null): number => {
    if (!epicRange) return 0;
    return Math.max(1, differenceInCalendarDays(epicRange.end, epicRange.start) + 1);
  };

  const isTaskOnDay = (task: Task, day: Date): boolean => {
    const start = getTaskStartDate(task);
    const end = getTaskEndDate(task);
    return day >= start && day <= end;
  };

  const getTaskPosition = (task: Task, day: Date): 'start' | 'middle' | 'end' | null => {
    const start = getTaskStartDate(task);
    const end = getTaskEndDate(task);
    
    if (isSameDay(day, start) && isSameDay(day, end)) return 'start';
    if (isSameDay(day, start)) return 'start';
    if (isSameDay(day, end)) return 'end';
    if (day > start && day < end) return 'middle';
    return null;
  };

  const toggleEpic = (epicId: string) => {
    const newExpanded = new Set(expandedEpics);
    if (newExpanded.has(epicId)) {
      newExpanded.delete(epicId);
    } else {
      newExpanded.add(epicId);
    }
    setExpandedEpics(newExpanded);
  };

  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-semibold text-foreground">Gantt Chart</h1>
            <p className="text-sm text-muted-foreground">{project.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {isValid(startDate) && isValid(endDate) 
                ? `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')} (${totalDurationDays}d)`
                : "Loading schedule..."}
            </span>
          </div>
        </div>
      </div>

      {/* Gantt Chart Container */}
      <SurfaceCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Month Headers */}
            <div className="flex border-border bg-muted/50">
              <div className="w-64 flex-shrink-0 border-r border-border p-2"></div>
              <div className="flex">
                {timelineDays.map((day, index) => {
                  const showMonthLabel =
                    index === 0 ||
                    format(day, 'yyyy-MM') !== format(timelineDays[index - 1], 'yyyy-MM');

                  return (
                    <div
                      key={`${format(day, 'yyyy-MM-dd')}-month`}
                      className="w-8 flex-shrink-0 border-r border-border py-1 text-center text-[10px] font-medium text-muted-foreground"
                      title={format(day, 'MMMM yyyy')}
                    >
                      {showMonthLabel ? format(day, 'MMM') : ''}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Timeline Header */}
            <div className="flex border-border bg-muted/30">
              <div className="w-64 flex-shrink-0 border-r border-border p-3 font-semibold text-foreground">
                Task Name
              </div>
              <div className="flex">
                {timelineDays.map((day, index) => (
                  <div
                    key={index}
                    className={`w-8 flex-shrink-0 border-r border-border/50 p-1 text-center text-xs ${
                      isSameDay(day, today) ? TODAY_HIGHLIGHT_CLASS : ''
                    }`}
                  >
                    <div className="font-medium text-foreground">{format(day, 'd')}</div>
                    <div className="text-muted-foreground">{format(day, 'EEE').charAt(0)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tasks by Epic */}
            {(projectEpics ?? []).map(epic => {
              const epicTasks = (projectTasks ?? []).filter(t => t.epicId === epic.id);
              const isExpanded = expandedEpics.has(epic.id);
              const epicRange = getEpicRange(epic, epicTasks);
              const epicDurationDays = getEpicDurationDays(epicRange);

              return (
                <div key={epic.id}>
                  {/* Epic Row */}
                  <div className="flex border-border transition-colors hover:bg-muted/30">
                    <div className="flex w-64 flex-shrink-0 items-center gap-2 border-r border-border p-3">
                      <button
                        onClick={() => toggleEpic(epic.id)}
                        className="text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <div>
                        <p className="font-medium text-primary">{epic.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {epicTasks.length} tasks{epicRange ? ` • ${epicDurationDays}d` : ''}
                        </p>
                        {epicRange && (
                          <p className="mt-0.5 text-xs text-muted-foreground/80">
                            {format(epicRange.start, 'MMM d')} - {format(epicRange.end, 'MMM d')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex">
                      {timelineDays.map((day, index) => (
                        <div
                          key={index}
                          className={`${GRID_CELL_CLASS} ${
                            isSameDay(day, today) ? TODAY_HIGHLIGHT_CLASS : ''
                          }`}
                        >
                          {epicRange && (() => {
                            const position = getRangePosition(epicRange, day);
                            if (!position) return null;

                            const isSingleDay =
                              isSameDay(day, epicRange.start) && isSameDay(day, epicRange.end);

                            return (
                              <div
                                className={`h-4 w-full bg-primary/35 ${
                                  isSingleDay
                                    ? 'rounded'
                                    : position === 'start'
                                      ? 'rounded-l'
                                      : position === 'end'
                                        ? 'rounded-r'
                                        : ''
                                }`}
                                title={`${epic.title} • ${epicDurationDays}d • ${format(epicRange.start, 'MMM d')} - ${format(epicRange.end, 'MMM d')}`}
                              />
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Epic Tasks */}
                  {isExpanded && epicTasks.map(task => (
                    <div key={task.id} className="flex border-border/60 transition-colors hover:bg-muted/20">
                      <div className="w-64 flex-shrink-0 border-r border-border p-3 pl-10">
                        {(() => {
                          const durationDays = getTaskDurationDays(task);
                          const rangeStart = getTaskStartDate(task);
                          const rangeEnd = getTaskEndDate(task);

                          return (
                            <>
                              <Link 
                                to={`/task/${task.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open task in a new tab"
                                  className="text-sm text-foreground transition-colors hover:text-primary"
                              >
                                {task.title}
                              </Link>
                              <p className="mt-1 text-xs text-muted-foreground">{task.status} • {durationDays}d</p>
                              <p className="mt-0.5 text-xs text-muted-foreground/80">
                                {format(rangeStart, 'MMM d')} - {format(rangeEnd, 'MMM d')}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                      <div className="flex">
                        {timelineDays.map((day, index) => (
                          <div
                            key={index}
                            className={`${GRID_CELL_CLASS} ${
                              isSameDay(day, today) ? TODAY_HIGHLIGHT_CLASS : ''
                            }`}
                          >
                            {(() => {
                              const rangeStart = getTaskStartDate(task);
                              const rangeEnd = getTaskEndDate(task);
                              const durationDays = getTaskDurationDays(task);
                              const position = getTaskPosition(task, day);
                              if (!position) return null;

                              const isSingleDay = isSameDay(rangeStart, rangeEnd) && isSameDay(day, rangeStart);

                              return (
                                <div
                                  className={`h-6 w-full ${getStatusColors(task.status).timeline} ${
                                    isSingleDay
                                      ? 'rounded'
                                      : position === 'start'
                                        ? 'rounded-l'
                                        : position === 'end'
                                          ? 'rounded-r'
                                          : ''
                                  }`}
                                  title={`${task.title} (${task.status}) • ${durationDays}d • ${format(rangeStart, 'MMM d')} - ${format(rangeEnd, 'MMM d')}`}
                                />
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Tasks without Epic */}
            {(projectTasks ?? []).filter(t => !t.epicId).length > 0 && (
              <>
                <div className="flex border-border bg-muted/30">
                  <div className="w-64 flex-shrink-0 border-r border-border p-3 font-medium text-foreground">
                    Unassigned Tasks
                  </div>
                  <div className="flex">
                    {timelineDays.map((day, index) => (
                      <div
                        key={index}
                        className={`w-8 flex-shrink-0 border-r border-border/50 ${
                          isSameDay(day, today) ? TODAY_HIGHLIGHT_CLASS : ''
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {(projectTasks ?? [])
                  .filter(t => !t.epicId)
                  .map(task => (
                    <div key={task.id} className="flex border-border/60 transition-colors hover:bg-muted/20">
                      <div className="w-64 flex-shrink-0 border-r border-border p-3">
                        {(() => {
                          const durationDays = getTaskDurationDays(task);
                          const rangeStart = getTaskStartDate(task);
                          const rangeEnd = getTaskEndDate(task);

                          return (
                            <>
                              <Link 
                                to={`/task/${task.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open task in a new tab"
                                  className="text-sm text-foreground transition-colors hover:text-primary"
                              >
                                {task.title}
                              </Link>
                              <p className="mt-1 text-xs text-muted-foreground">{task.status} • {durationDays}d</p>
                              <p className="mt-0.5 text-xs text-muted-foreground/80">
                                {format(rangeStart, 'MMM d')} - {format(rangeEnd, 'MMM d')}
                              </p>
                            </>
                          );
                        })()}
                      </div>
                      <div className="flex">
                        {timelineDays.map((day, index) => (
                          <div
                            key={index}
                            className={`${GRID_CELL_CLASS} ${
                              isSameDay(day, today) ? TODAY_HIGHLIGHT_CLASS : ''
                            }`}
                          >
                            {(() => {
                              const rangeStart = getTaskStartDate(task);
                              const rangeEnd = getTaskEndDate(task);
                              const durationDays = getTaskDurationDays(task);
                              const position = getTaskPosition(task, day);
                              if (!position) return null;

                              const isSingleDay = isSameDay(rangeStart, rangeEnd) && isSameDay(day, rangeStart);

                              return (
                                <div
                                  className={`h-6 w-full ${getStatusColors(task.status).timeline} ${
                                    isSingleDay
                                      ? 'rounded'
                                      : position === 'start'
                                        ? 'rounded-l'
                                        : position === 'end'
                                          ? 'rounded-r'
                                          : ''
                                  }`}
                                  title={`${task.title} (${task.status}) • ${durationDays}d • ${format(rangeStart, 'MMM d')} - ${format(rangeEnd, 'MMM d')}`}
                                />
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </>
            )}

            {/* Empty State */}
            {(projectTasks ?? []).length === 0 && (
              <div className="py-12 text-center text-muted-foreground">
                No tasks to display in Gantt chart
              </div>
            )}
          </div>
        </div>
      </SurfaceCard>

      {/* Legend */}
      <SurfaceCard className="mt-6 p-4">
        <h3 className="mb-3 font-semibold text-foreground">Status Legend</h3>
        <div className="flex flex-wrap gap-4">
          {TASK_LEGEND_STATUSES.map((status) => (
            <div key={status} className="flex items-center gap-2">
              <div className={`h-4 w-4 rounded ${getStatusColors(status).timeline}`} />
              <span className="text-sm text-muted-foreground">{status}</span>
            </div>
          ))}
        </div>
      </SurfaceCard>
    </div>
  );
}
